import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mockProjects } from '@/data/projectsData';
import type { ProjectDetails, ProjectTeamMember } from '@/types/project';
import { useDataSource, type DataMode } from '@/contexts/DataSourceContext';
import { projectsApi, type ProjectListItem, type ProjectStatsResponse, type PaginationInfo } from '@/services/projectsApi';
import type { CostCentre } from '@/types/timesheet';
import { toast } from 'sonner';
import { debounce } from '@/utils/debounce';
import { mapListItemToDetails } from '@/utils/projectMappers';
import {
  DEFAULT_PROJECT_STATS,
  computeDemoStats,
  filterAndSortMockProjects,
} from '@/components/Pages/ActiveProjects/activeProjectsSelectors';
import { useProjectDetailsDialog } from '@/components/Pages/ActiveProjects/useProjectDetailsDialog';

export const ITEMS_PER_PAGE = 50;

export type ActiveMetric = 'total' | 'at-risk' | 'high-priority';
export type EmptyStateType = 'no-data' | 'no-search' | 'no-filters';

export interface UseActiveProjectsResult {
  searchQuery: string;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  priorityFilter: string;
  setPriorityFilter: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  selectedProject: ProjectDetails | null;
  showDetailsDialog: boolean;
  setShowDetailsDialog: (open: boolean) => void;
  projectCostCentres: CostCentre[];
  setProjectCostCentres: React.Dispatch<React.SetStateAction<CostCentre[]>>;
  isLoadingCostCentres: boolean;
  isLoadingTeamMembers: boolean;
  apiProjects: ProjectListItem[];
  pagination: PaginationInfo;
  isLoading: boolean;
  isLoadingStats: boolean;
  displayProjects: ProjectDetails[];
  displayStats: ProjectStatsResponse;
  activeMetric: ActiveMetric | null;
  projectIdFilter: string | null;
  setProjectIdFilter: (value: string | null) => void;
  hasActiveFilters: boolean;
  getEmptyStateType: () => EmptyStateType;
  screenMode: DataMode;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClearSearch: () => void;
  handlePageChange: (newPage: number) => void;
  handleMetricCardClick: (metric: ActiveMetric) => void;
  handleViewDetails: (project: ProjectDetails | ProjectListItem) => void;
  handleClearAllFilters: () => void;
  handleExportProjects: () => void;
}

/**
 * Data, filtering and fetching logic for the Active Projects page.
 *
 * Purely presentational state (`viewMode`, `showProjectManagementModal`) is
 * deliberately left in the page component since it is only read by the render
 * tree and never participates in fetching.
 */
export const useActiveProjects = (): UseActiveProjectsResult => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('projects-active');
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');

  // Server-side pagination state
  const [apiProjects, setApiProjects] = useState<ProjectListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: ITEMS_PER_PAGE,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [stats, setStats] = useState<ProjectStatsResponse>({
    totalProjects: 0,
    activeProjects: 0,
    atRiskCount: 0,
    highPriorityCount: 0,
    totalTeamMembers: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // For demo mode, use mock projects with client-side filtering
  const mockProjectsFiltered = useMemo(() => {
    if (screenMode !== 'demo') return [];
    return filterAndSortMockProjects(searchQuery, statusFilter, priorityFilter, sortBy);
  }, [screenMode, searchQuery, statusFilter, priorityFilter, sortBy]);

  // Track the current search term for server-side search
  const currentSearchRef = useRef(searchQuery);

  // Fetch projects with server-side pagination (live mode only)
  const fetchProjects = useCallback((page: number = 1, search?: string) => {
    if (screenMode !== 'live') return;

    setIsLoading(true);
    const params = {
      page,
      pageSize: ITEMS_PER_PAGE,
      search: search ?? currentSearchRef.current,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      priority: priorityFilter !== 'all' ? priorityFilter : undefined,
      sortBy: sortBy,
      sortOrder: 'asc' as const,
    };

    projectsApi.getProjects(params)
      .then(response => {
        setApiProjects(response.data);
        setPagination(response.pagination);
      })
      .catch(error => {
        console.error('Failed to fetch projects:', error);
        toast.error('Failed to load projects', {
          description: 'Could not connect to the API. Please check your connection and try again.',
        });
        setApiProjects([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [screenMode, statusFilter, priorityFilter, sortBy]);

  // Fetch project stats (live mode only)
  const fetchStats = useCallback(() => {
    if (screenMode !== 'live') return;

    setIsLoadingStats(true);
    projectsApi.getProjectStats()
      .then(response => {
        setStats(response);
      })
      .catch(error => {
        console.error('Failed to fetch project stats:', error);
      })
      .finally(() => {
        setIsLoadingStats(false);
      });
  }, [screenMode]);

  // Fetch projects and stats on mount and when screen mode changes
  useEffect(() => {
    fetchProjects(1);
    fetchStats();
  }, [fetchProjects, fetchStats]);

  // Listen for company-changed event to refetch data when switching companies.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;
        fetchProjects(1);
        fetchStats();
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [fetchProjects, fetchStats]);

  // Store the project ID filter separately from search query
  const [projectIdFilter, setProjectIdFilter] = useState<string | null>(null);

  // Apply project filter from URL parameters on mount
  useEffect(() => {
    const projectIdFromUrl = searchParams.get('projectId');
    if (projectIdFromUrl) {
      // Set the project ID filter to show only this specific project
      setProjectIdFilter(projectIdFromUrl);

      // Clear the URL parameter after applying the filter
      // This keeps the URL clean while maintaining the filtered state
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('projectId');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Debounced search handler - triggers server-side search in live mode
  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      currentSearchRef.current = value;
      if (screenMode === 'live') {
        fetchProjects(1, value);
      }
    }, 300),
    [screenMode, fetchProjects]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    currentSearchRef.current = '';
    if (screenMode === 'live') {
      fetchProjects(1, '');
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (screenMode === 'live') {
      fetchProjects(newPage);
    }
  };

  // Handler for metric card clicks - synchronizes with filter dropdowns
  const handleMetricCardClick = (metric: ActiveMetric) => {
    // Check if this metric is already active
    const isActive = 
      (metric === 'total' && statusFilter === 'all' && priorityFilter === 'all') ||
      (metric === 'at-risk' && statusFilter === 'at-risk') ||
      (metric === 'high-priority' && priorityFilter === 'high');

    if (isActive) {
      // Clear all filters - will trigger re-fetch via useEffect
      setStatusFilter('all');
      setPriorityFilter('all');
    } else {
      // Apply the selected metric filter - will trigger re-fetch via useEffect
      if (metric === 'total') {
        setStatusFilter('all');
        setPriorityFilter('all');
      } else if (metric === 'at-risk') {
        setStatusFilter('at-risk');
        setPriorityFilter('all');
      } else if (metric === 'high-priority') {
        setStatusFilter('all');
        setPriorityFilter('high');
      }
    }
  };

  // Determine active metric based on current filters
  const getActiveMetric = (): ActiveMetric | null => {
    if (statusFilter === 'at-risk' && priorityFilter === 'all') {
      return 'at-risk';
    } else if (statusFilter === 'all' && priorityFilter === 'high') {
      return 'high-priority';
    } else if (statusFilter === 'all' && priorityFilter === 'all') {
      return 'total';
    }
    return null;
  };

  const activeMetric = getActiveMetric();

  // Get the display projects based on mode
  // In live mode: use apiProjects (already filtered/paginated by server), mapped to ProjectDetails format
  // In demo mode: use mockProjectsFiltered (client-side filtered, already ProjectDetails)
  const displayProjects: ProjectDetails[] = useMemo(() => {
    if (screenMode === 'live') {
      return apiProjects.map(mapListItemToDetails);
    }
    return mockProjectsFiltered;
  }, [screenMode, apiProjects, mockProjectsFiltered]);

  // For demo mode stats calculation
  const demoStats = useMemo(() => {
    if (screenMode !== 'demo') return null;
    return computeDemoStats();
  }, [screenMode]);

  // Get stats based on mode - always provide a fallback to prevent undefined errors
  const displayStats = screenMode === 'live' ? (stats || DEFAULT_PROJECT_STATS) : (demoStats || DEFAULT_PROJECT_STATS);

  const handleTeamMembersLoaded = useCallback((projectId: string, teamMembers: ProjectTeamMember[]) => {
    // Reflect the live team count on the matching list item so the card stays
    // in sync with the backend-persisted Project.TeamSize without a full list
    // reload.
    setApiProjects(prev =>
      prev.map(item =>
        item.id === projectId ? { ...item, teamSize: teamMembers.length } : item
      )
    );
  }, []);

  const {
    selectedProject,
    showDetailsDialog,
    setShowDetailsDialog,
    projectCostCentres,
    setProjectCostCentres,
    isLoadingCostCentres,
    isLoadingTeamMembers,
    handleViewDetails,
  } = useProjectDetailsDialog({ screenMode, onTeamMembersLoaded: handleTeamMembersLoaded });

  // Check if any filters are active
  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || priorityFilter !== 'all' || projectIdFilter !== null;

  // Clear all filters
  const handleClearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setProjectIdFilter(null);
  };

  // Export functionality
  const handleExportProjects = () => {
    toast.success('Export started', {
      description: 'Your project list is being prepared for download.',
    });
    // TODO: Implement actual export logic
  };

  // Determine empty state type
  const getEmptyStateType = (): EmptyStateType => {
    const totalCount = screenMode === 'live' ? pagination.totalItems : mockProjects.length;
    if (totalCount === 0 && !hasActiveFilters) return 'no-data';
    if (searchQuery !== '' && displayProjects.length === 0) return 'no-search';
    if (hasActiveFilters && displayProjects.length === 0) return 'no-filters';
    return 'no-data';
  };

  return {
    searchQuery,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    sortBy,
    setSortBy,
    selectedProject,
    showDetailsDialog,
    setShowDetailsDialog,
    projectCostCentres,
    setProjectCostCentres,
    isLoadingCostCentres,
    isLoadingTeamMembers,
    apiProjects,
    pagination,
    isLoading,
    isLoadingStats,
    displayProjects,
    displayStats,
    activeMetric,
    projectIdFilter,
    setProjectIdFilter,
    hasActiveFilters,
    getEmptyStateType,
    screenMode,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleMetricCardClick,
    handleViewDetails,
    handleClearAllFilters,
    handleExportProjects,
  };
};
